import { orderBy, padStart, startCase } from 'lodash';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { Link, Route } from 'react-router-dom';

import { CrewMember, User } from '@legacy-types';

import { useEvent } from '@legacy-providers/event';

import { getCrewMembers } from '@shared/crew-members/endpoints/getCrewMembers';
import { Date } from '@shared/ui/components/Date';
import { Icon } from '@shared/ui/components/Icon';
import { Portlet } from '@shared/ui/components/Portlet';
import { getUser } from '@shared/users/endpoints/getUser';

import { AdminWrapper } from '@admin/layout/components/AdminWrapper';
import { ShowUserRegistration } from '@admin/users/views/ShowUser/ShowUserRegistration';
import { ShowUserRegistrationLink } from '@admin/users/views/ShowUser/ShowUserRegistrationLink';

export function ShowUser() {
  const history = useHistory();
  const params = useParams<{ id: string; registration_id?: string }>();

  const event = useEvent();

  const [user, setUser] = useState<User | null>(null);
  const [crewMembers, setCrewMembers] = useState<CrewMember[]>([]);

  function redirectToFirstRegistration(registrationId?: number) {
    if (!registrationId) {
      history.push(`/admin/users/${params.id}`);
    } else {
      history.push(`/admin/users/${params.id}/registrations/${registrationId}`);
    }
  }

  useEffect(() => {
    Promise.all([
      getUser({ id: parseInt(params.id) }, { include: ['companies'] }),
      getCrewMembers({
        filter: { event_id: event.id, user_id: params.id },
        include: ['user', 'registration.company'],
      }),
    ]).then(([user, crewMembers]) => {
      setUser(user);

      setCrewMembers(orderBy(crewMembers.data, (crewMember) => crewMember.registration!.company.name));

      if (!params.registration_id && crewMembers.data.length > 0) {
        redirectToFirstRegistration(crewMembers.data[0].registrationId);
      }

      if (params.registration_id) {
        const registrationExists = crewMembers.data.find(
          (crewMember) => crewMember.registrationId === parseInt(params.registration_id!)
        );

        if (!registrationExists) {
          redirectToFirstRegistration(crewMembers.data[0]?.registrationId);
        }
      }
    });
  }, [params.id]);

  if (!user) {
    return null;
  }

  return (
    <AdminWrapper innerClassName="grid grid-cols-[minmax(0,1fr)] 2xl:grid-cols-[1fr_minmax(0,2fr)] gap-8 items-start">
      <aside className="2xl:sticky top-[7rem] grid grid-cols-1 md:grid-cols-2 gap-8 2xl:grid-cols-1">
        <Portlet className="relative">
          <div className="relative z-20">
            <p>
              <strong className="text-2xl font-medium text-gray-700">{user.fullName}</strong>
              <Link
                to={`/admin/employees/${user.id}/edit`}
                className="inline-block ml-2 text-lg text-gray-500 hover:text-gray-800"
              >
                <Icon icon="pencil" />
              </Link>
            </p>
            <dl className="mt-8 grid grid-cols-[auto_auto] gap-x-4 gap-y-1 [&>dt]:text-gray-500 [&>dt]:font-medium">
              <dt>Email</dt>
              <dd>{user.email}</dd>
              <dt>Nationality</dt>
              <dd>{user.nationality}</dd>
              <dt>Registry No.</dt>
              <dd>{padStart(user.nationalRegistryNumberLastDigits, 10, 'x')}</dd>
              <dt>Phone</dt>
              <dd>{user.telephone}</dd>
              <dt>Gender</dt>
              <dd>{startCase(user.gender)}</dd>
              <dt>T-shirt Size</dt>
              <dd>{user.size}</dd>
              <dt>Date of Birth</dt>
              <dd>
                <Date date={user.birthDate} />
              </dd>
            </dl>
          </div>
          <div className="absolute inset-0 overflow-hidden">
            <Icon
              icon="user"
              className="absolute -top-8 -right-8 text-[100px] text-indigo-300 bg-indigo-50 rounded-full p-6"
            />
          </div>
        </Portlet>
        {crewMembers.length > 1 && (
          <section className="pt-4">
            <p className="uppercase font-semibold text-gray-500 text-sm pl-8">Registrations</p>
            <ul className="divide-y">
              {crewMembers.map((crewMember) => {
                const company = user.companies.find((company) => company.id === crewMember.registration!.companyId);

                if (!company) {
                  return null;
                }

                return <ShowUserRegistrationLink key={crewMember.id} crewMember={crewMember} company={company} />;
              })}
            </ul>
          </section>
        )}
      </aside>
      <Route
        key="base"
        exact
        path="/admin/users/:id/registrations/:registration_id"
        render={({ match }) => {
          const crewMember = crewMembers.find(
            (crewMember) => crewMember.registrationId === parseInt(match.params.registration_id)
          );

          const company = user.companies.find((company) => company.id === crewMember?.registration!.companyId);

          if (!company || !crewMember) {
            return null;
          }

          return <ShowUserRegistration company={company} crewMember={crewMember} />;
        }}
      />
    </AdminWrapper>
  );
}
